import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { PersonAccessSection, type PersonAccessGrant, type PersonAccessSystem } from "./PersonAccessSection";
import { PersonHubRoles, type PersonHubRole } from "./PersonHubRoles";
import { PersonJobDescriptions, type PersonJobDescription } from "./PersonJobDescriptions";
import { PersonDisciplinarySection, type DisciplinaryEvent } from "./PersonDisciplinarySection";
import { PersonTrainingSection, type PersonTrainingRow } from "./PersonTrainingSection";

type FieldSource = "hub" | "quickbooks" | "unset";

type Employment = {
  jobTitle: string | null;
  jobTitleSource: FieldSource;
  department: string | null;
  managerStaffId: number | null;
  hiredOn: string | null;
  hiredOnSource: FieldSource;
  terminatedOn: string | null;
  terminatedOnSource: FieldSource;
  qboEmployeeId: string | null;
  qboSyncedAt: string | null;
  jobSummary: string | null;
};

type PersonPayload = {
  staffId: number;
  fullName: string;
  email: string | null;
  personalEmail: string | null;
  phone: string | null;
  active: boolean;
  hubUserId: string | null;
  quickbooksTimeClockMatched?: boolean;
  canEditHubRoles?: boolean;
  hubRoleCatalog: PersonHubRole[] | null;
  employment: Employment | null;
  jobDescriptions: PersonJobDescription[];
  grants: PersonAccessGrant[];
  systems: PersonAccessSystem[];
  roles: PersonHubRole[];
  managerChoices: { staffId: number; fullName: string; active: boolean }[];
  training: PersonTrainingRow[];
  disciplinary: { canView: boolean; events: DisciplinaryEvent[] };
};

type EmploymentDraft = {
  jobTitle: string;
  department: string;
  managerStaffId: string;
  hiredOn: string;
  terminatedOn: string;
  jobSummary: string;
  personalEmail: string;
  phone: string;
};

const EMPTY_EMPLOYMENT: Employment = {
  jobTitle: null,
  jobTitleSource: "unset",
  department: null,
  managerStaffId: null,
  hiredOn: null,
  hiredOnSource: "unset",
  terminatedOn: null,
  terminatedOnSource: "unset",
  qboEmployeeId: null,
  qboSyncedAt: null,
  jobSummary: null,
};

function draftFromPerson(person: PersonPayload): EmploymentDraft {
  const employment = person.employment;
  return {
    jobTitle: employment?.jobTitle ?? "",
    department: employment?.department ?? "",
    managerStaffId: employment?.managerStaffId != null ? String(employment.managerStaffId) : "",
    hiredOn: employment?.hiredOn ?? "",
    terminatedOn: employment?.terminatedOn ?? "",
    jobSummary: employment?.jobSummary ?? "",
    personalEmail: person.personalEmail ?? "",
    phone: person.phone ?? "",
  };
}

function sourceLabel(source: FieldSource): string {
  if (source === "quickbooks") return "QuickBooks";
  if (source === "hub") return "Saved in hub";
  return "Not set";
}

export function PersonPage() {
  const params = useParams();
  const staffId = Number(params.staffId);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("people");
  const readOnlyHint = "Your role has read-only access to the People module.";
  const [draft, setDraft] = useState<EmploymentDraft | null>(null);

  const personQuery = useQuery({
    queryKey: ["people", "staff", staffId],
    queryFn: () => apiFetch<PersonPayload>(`/api/people/staff/${staffId}`),
    enabled: Number.isFinite(staffId) && staffId > 0,
  });

  useEffect(() => {
    if (personQuery.data) {
      setDraft(draftFromPerson(personQuery.data));
    }
  }, [personQuery.data]);

  const savedDraft = useMemo(
    () => (personQuery.data ? draftFromPerson(personQuery.data) : null),
    [personQuery.data],
  );
  const dirty = draft != null && savedDraft != null && JSON.stringify(draft) !== JSON.stringify(savedDraft);
  const employment = personQuery.data?.employment ?? EMPTY_EMPLOYMENT;

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/people/staff/${staffId}/employment`, {
        method: "PUT",
        body: {
          jobTitle: draft?.jobTitle ?? "",
          department: draft?.department ?? "",
          managerStaffId: draft?.managerStaffId === "" ? null : Number(draft?.managerStaffId),
          hiredOn: draft?.hiredOn ?? "",
          terminatedOn: draft?.terminatedOn ?? "",
          jobSummary: draft?.jobSummary ?? "",
          personalEmail: draft?.personalEmail ?? "",
          phone: draft?.phone ?? "",
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["people", "staff", staffId] });
      await queryClient.invalidateQueries({ queryKey: ["people", "directory"] });
      showToast({ message: "Employment saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ matched: number; titleFieldPresent: boolean; unmatched: { displayName: string }[] }>(
        "/api/people/quickbooks/sync",
        { method: "POST" },
      ),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["people"] });
      const titleNote = result.titleFieldPresent
        ? "Job titles were copied when QuickBooks sent them."
        : "QuickBooks did not send job titles. Enter titles here.";
      showToast({
        message: `Matched ${result.matched} people. ${result.unmatched.length} QuickBooks employees had no staff email match. ${titleNote}`,
        tone: "success",
      });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  if (!Number.isFinite(staffId) || staffId <= 0) {
    return <p className="text-sm text-rose-700">This person link is not valid.</p>;
  }

  const person = personQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel={person?.fullName ?? "Person"}
          hoverHint="Hire date and termination date can be filled from the QuickBooks Online employee list when the staff email matches. Department and manager are entered here. A field you edit stays as you saved it."
        >
          {person?.fullName ?? "Person"}
        </PageTitleWithHelp>
        <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
          <Button
            type="button"
            variant="secondary"
            disabled={!writesAllowed || syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {syncMutation.isPending ? "Syncing QuickBooks…" : "Sync from QuickBooks"}
          </Button>
        </ReadOnlyHoverLayer>
      </div>

      {personQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {(personQuery.error as Error).message}
        </p>
      ) : null}

      {person && draft ? (
        <>
          <PersonHubRoles
            staffId={staffId}
            hubUserId={person.hubUserId}
            roles={person.roles}
            canEditHubRoles={person.canEditHubRoles === true}
            hubRoleCatalog={person.hubRoleCatalog}
            quickbooksTimeClockMatched={person.quickbooksTimeClockMatched === true}
          />
          <HubSettingsSection
            title="Employment"
            hoverHint="Department and manager are not taken from QuickBooks. Title, hire date, and termination date show QuickBooks until you save a different value."
            footer={
              <HubSaveButton
                dirty={dirty}
                disabled={!writesAllowed}
                blockedHint={readOnlyHint}
                isLoading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                Save employment
              </HubSaveButton>
            }
          >
            <div className="mb-3 space-y-1 text-sm text-slate-700">
              <p>
                {person.email ?? "No email"} · {person.active ? "Active staff" : "Inactive staff"}
                {employment.qboEmployeeId ? ` · QuickBooks employee ${employment.qboEmployeeId}` : ""}
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="people-personal-email">Personal email</Label>
                <Input
                  id="people-personal-email"
                  className="mt-1"
                  value={draft.personalEmail}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, personalEmail: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="people-phone">Phone</Label>
                <Input
                  id="people-phone"
                  className="mt-1"
                  value={draft.phone}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="people-department">Department</Label>
                <Input
                  id="people-department"
                  className="mt-1"
                  value={draft.department}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, department: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="people-manager">Manager</Label>
                <select
                  id="people-manager"
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                  value={draft.managerStaffId}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, managerStaffId: event.target.value })}
                >
                  <option value="">No manager</option>
                  {person.managerChoices.map((choice) => (
                    <option key={choice.staffId} value={choice.staffId}>
                      {choice.fullName}
                      {choice.active ? "" : " (inactive)"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="people-hired">Hired ({sourceLabel(employment.hiredOnSource)})</Label>
                <Input
                  id="people-hired"
                  type="date"
                  className="mt-1"
                  value={draft.hiredOn}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, hiredOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="people-terminated">Terminated ({sourceLabel(employment.terminatedOnSource)})</Label>
                <Input
                  id="people-terminated"
                  type="date"
                  className="mt-1"
                  value={draft.terminatedOn}
                  disabled={!writesAllowed}
                  onChange={(event) => setDraft({ ...draft, terminatedOn: event.target.value })}
                />
              </div>
            </div>
          </HubSettingsSection>

          <PersonJobDescriptions
            staffId={person.staffId}
            descriptions={person.jobDescriptions ?? []}
            writesAllowed={writesAllowed}
            readOnlyHint={readOnlyHint}
          />

          <PersonAccessSection
            staffId={person.staffId}
            grants={person.grants}
            systems={person.systems}
            writesAllowed={writesAllowed}
            readOnlyHint={readOnlyHint}
          />

          <PersonTrainingSection
            staffId={person.staffId}
            training={person.training}
            managerChoices={person.managerChoices}
            writesAllowed={writesAllowed}
            readOnlyHint={readOnlyHint}
          />

          {person.disciplinary.canView ? (
            <PersonDisciplinarySection
              staffId={person.staffId}
              events={person.disciplinary.events}
              writesAllowed={writesAllowed}
              readOnlyHint={readOnlyHint}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
